import React from 'react';
import { BackupRecordRepresentation, SearchBackupRequest } from '@common/api';
import { CheckboxField, Option, SearchInput, SelectInput } from '@components/form';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { Modal } from '@components/modal/Modal';
import { ModalFooter } from '@components/modal/ModalFooter';
import { useModal } from '@components/modal/ModalProvider';
import { PageLayout } from '@components/page/PageLayout';
import { TableList, TableListColumn } from '@components/table-list/TableList';
import {
    useCreateBackup,
    useDownloadBackup,
    useInvalidateBackups,
    useRestoreBackup,
    useSearchBackups,
    useUpdateBackupDescription,
    useUploadBackup,
} from '@data/backup/backupActions';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import { UseMutationResult } from '@tanstack/react-query';
import { PAGE_ICONS } from '@util/folderItems';
import { formatBytes } from '@util/formatNum';
import { downloadFile } from '@util/helpers';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../components/button';
import { styled } from 'goober';

const ListContainer = styled('div')`
    gap: 16px;
    flex-grow: 1;
    display: flex;
    align-items: start;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;
    min-height: 0;
`;

const DescriptionInput = styled('input')`
    width: 100%;
    border: 1px solid transparent;
    background: transparent;
    font-size: 12px;
    padding: 2px 4px;
    border-radius: 3px;
    color: inherit;
    outline: none;
    &:hover {
        border-color: var(--color-border, #ccc);
    }
    &:focus {
        border-color: var(--color-primary, #4a90d9);
        background: var(--color-bg-input, #fff);
    }
`;

function EditableDescription({
    row,
    canEdit,
    onSave,
}: {
    row: BackupRecordRepresentation;
    canEdit: boolean;
    onSave: (id: number, description: string | undefined) => void;
}) {
    const [value, setValue] = useState(row.description ?? '');
    const [dirty, setDirty] = useState(false);

    const handleBlur = () => {
        if (!dirty) return;
        const trimmed = value.trim() || undefined;
        if (trimmed !== (row.description ?? undefined)) {
            onSave(row.id!, trimmed);
        }
        setDirty(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') {
            setValue(row.description ?? '');
            setDirty(false);
            (e.target as HTMLInputElement).blur();
        }
    };

    if (!canEdit) {
        return <span style={{ opacity: row.description ? 1 : 0.4 }}>{row.description || '-'}</span>;
    }

    return (
        <DescriptionInput
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
                setDirty(true);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="-"
            maxLength={500}
            onClick={(e) => e.stopPropagation()}
        />
    );
}

function CreateBackupConfirmationModal() {
    const { LL } = useI18nContext();
    const { hideModal } = useModal();
    const createBackup = useCreateBackup();
    const refreshBackupsList = useInvalidateBackups();

    useEffect(() => {
        if (createBackup.isSuccess) {
            refreshBackupsList();
            hideModal();
            toast.success(LL.BACKUP_CREATED());
        }
    }, [LL, createBackup, hideModal, refreshBackupsList]);

    return (
        <Modal.Content width={500}>
            <Modal.Header title={LL.CREATE_BACKUP()} />
            <Modal.Body>
                <Modal.Paragraph>{LL.CREATE_BACKUP_WARNING()}</Modal.Paragraph>
            </Modal.Body>
            <Modal.Footer
                loading={createBackup.isLoading}
                done={() => createBackup.mutateAsync()}
                doneLabel={LL.CREATE()}
            />
        </Modal.Content>
    );
}

function RestoreBackupConfirmationModal({ backupPath }: { backupPath: string }) {
    const { LL } = useI18nContext();
    const { hideModal } = useModal();
    const restoreBackup = useRestoreBackup();

    useEffect(() => {
        if (restoreBackup.isSuccess) {
            hideModal();
            toast.success(LL.BACKUP_RESTORED());
        }
    }, [LL, restoreBackup, hideModal]);

    return (
        <Modal.Content width={500}>
            <Modal.Header title={LL.RESTORE_BACKUP()} />
            <Modal.Body>
                <Modal.Paragraph>{LL.RESTORE_BACKUP_WARNING({ backupPath: backupPath })}</Modal.Paragraph>
            </Modal.Body>
            <ModalFooter
                loading={restoreBackup.isLoading}
                done={() => restoreBackup.mutateAsync(backupPath)}
                doneLabel={LL.RESTORE()}
            />
        </Modal.Content>
    );
}

const MONTHS_NUMERIC: Option<number | null>[] = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
    { label: '9', value: 9 },
    { label: '10', value: 10 },
    { label: '11', value: 11 },
    { label: '12', value: 12 },
];

const DEFAULT_PAGE_SIZE = 50;

const initRequest: SearchBackupRequest = {
    pageSize: DEFAULT_PAGE_SIZE,
    page: 0,
};

type BackupListProps = {
    download: UseMutationResult<Blob, unknown, string>;
};
function BackupList({ download }: BackupListProps) {
    const { LL } = useI18nContext();
    const { user } = useAuth();

    const [selectedYear, setSelectedYear] = useState<string>('');
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const monthOptions: Option<number | null>[] = [{ label: LL.ALL_MONTHS(), value: null }, ...MONTHS_NUMERIC];
    const [request, setRequest] = useState<SearchBackupRequest>(initRequest);
    const [showUploaded, setShowUploaded] = useState(true);
    const data = useSearchBackups(request);
    const { showModal } = useModal();
    const updateDescription = useUpdateBackupDescription();

    const canEditDescription = !!user?.classActions?.overseerClassActions?.canBackupTenant;

    const handleSaveDescription = (id: number, description: string | undefined) => {
        updateDescription.mutate({ id, description });
    };

    const handleYearInput = (value: string) => {
        const regexPattern = /^\d{1,4}$/;

        if (regexPattern.test(value) || value.length < 1) {
            setSelectedYear(value);
            setRequest({
                ...request,
                year: parseInt(value) || undefined,
            });
        }
    };

    const isYearValid = () => {
        const regexPattern = /^(19|20)\d{2}$/;
        return regexPattern.test(selectedYear);
    };

    const loadMore = () => {
        if (data?.pageable?.pageSize) {
            setRequest({ ...request, pageSize: data.pageable.pageSize + DEFAULT_PAGE_SIZE });
        }
    };

    const filteredData = useMemo(() => {
        const content = data?.content ?? [];
        const filtered = showUploaded
            ? content
            : content.filter(
                  (backup: BackupRecordRepresentation) => backup.filename.toLowerCase().indexOf('/upload/') < 0
              );
        return [...filtered].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }, [showUploaded, data]);

    function capitalizeWord(word: string) {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }

    function capitalizeWords(input: string): string {
        if (input.length === 0) return input;
        const words = input.split(' ');
        return words.map((word) => capitalizeWord(word)).join(' ');
    }

    const columns: TableListColumn<BackupRecordRepresentation>[] = [
        {
            title: LL.BACKUP(),
            width: '3fr',
            getValue: (row: BackupRecordRepresentation) => row.filename,
        },
        {
            title: LL.DESCRIPTION(),
            width: '2fr',
            getValue: (row: BackupRecordRepresentation) => (
                <EditableDescription row={row} canEdit={canEditDescription} onSave={handleSaveDescription} />
            ),
        },
        {
            title: LL.INITIATED_BY(),
            width: '2fr',
            getValue: (row: BackupRecordRepresentation) => row.initiatedById ?? '',
        },
        {
            title: LL.TYPE(),
            width: '1fr',
            getValue: (row: BackupRecordRepresentation) =>
                row.metadata ? capitalizeWords(JSON.parse(row.metadata).type ?? '') : '',
        },
        {
            title: LL.VERSION(),
            width: '1fr',
            getValue: (row: BackupRecordRepresentation) => row.farseerVersion ?? '',
        },
        {
            title: LL.CREATION_DATE(),
            width: '1fr',
            getValue: (row: BackupRecordRepresentation) => row.createdAt.toLocaleDateString('hr-hr'),
        },
        {
            title: LL.FILE_SIZE(),
            width: '1fr',
            getValue: (row: BackupRecordRepresentation) => formatBytes(row.size, 0),
        },
        {
            title: '',
            width: '1fr',
            getValue: (row: BackupRecordRepresentation) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                        <Button
                            $type="tertiary"
                            title={LL.DOWNLOAD()}
                            $size="small"
                            onClick={async () => {
                                const loadingId = toast.loading(LL.DOWNLOADING_BACKUP());
                                const backupBlob = await download.mutateAsync(row.filename);
                                downloadFile(backupBlob, row.filename);
                                toast.dismiss(loadingId);
                            }}
                            disabled={
                                !user?.classActions?.overseerClassActions?.canDownloadBackup || download.isLoading
                            }
                        />
                        <Button
                            $type="tertiary"
                            title={LL.RESTORE()}
                            $size="small"
                            onClick={() => showModal(<RestoreBackupConfirmationModal backupPath={row.filename} />)}
                            disabled={!user?.classActions?.overseerClassActions?.canRestoreTenant || download.isLoading}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <FlexCol $gap={16}>
            <FlexRow $gap={8}>
                <SearchInput
                    value={selectedYear}
                    name="restore-year"
                    onChange={handleYearInput}
                    placeholder={LL.YEAR()}
                    width={100}
                />
                <SelectInput
                    onChange={(value) => {
                        const foundMonth = monthOptions.find((month) => month.value === value);
                        if (foundMonth) {
                            setSelectedMonth(foundMonth.value as number);
                            setRequest({ ...request, month: foundMonth.value as number });
                        }
                    }}
                    options={monthOptions}
                    value={selectedMonth}
                    placeholder={LL.SELECT_MONTH()}
                    width={200}
                    disabled={!isYearValid()}
                />
                <CheckboxField label={LL.SHOW_UPLOADED()} value={showUploaded} onChange={setShowUploaded} />
            </FlexRow>
            <ListContainer style={{ height: '100%' }}>
                <TableList
                    rows={filteredData}
                    columns={columns}
                    keyExtractor={(row) => row.filename}
                    loadMore={!data?.last ? loadMore : undefined}
                />
            </ListContainer>
        </FlexCol>
    );
}

export default function BackupsAndRestore() {
    const { LL } = useI18nContext();
    const { showModal } = useModal();
    const { user, tenant } = useAuth();
    const downloadBackup = useDownloadBackup();
    const { uploadBackup, uploadingBackup } = useUploadBackup(tenant, LL);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const uploadBackupHandler = async () => {
        const inputElement = fileInputRef.current;
        if (inputElement === null) return;
        const backupFile = inputElement.files?.[0];

        if (backupFile !== undefined) await uploadBackup(backupFile);
    };

    const title = LL.CONFIG_MENU_SECTIONS['Backup & Restore']();

    const actions = (
        <>
            <Button
                title={LL.UPLOAD_BACKUP()}
                $type="tertiary"
                onClick={() => {
                    const inputElement = fileInputRef.current;
                    if (inputElement === null) return;

                    const uploadCallback = async () => {
                        await uploadBackupHandler();
                        inputElement.removeEventListener('change', uploadCallback);
                    };

                    inputElement.addEventListener('change', uploadCallback);
                    inputElement.click();
                }}
                disabled={
                    !user?.classActions?.overseerClassActions?.canBackupTenant ||
                    downloadBackup.isLoading ||
                    uploadingBackup
                }
            />
            <Button
                title={LL.CREATE_BACKUP()}
                onClick={() => showModal(<CreateBackupConfirmationModal />)}
                disabled={
                    !user?.classActions?.overseerClassActions?.canBackupTenant ||
                    downloadBackup.isLoading ||
                    uploadingBackup
                }
            />
        </>
    );

    return (
        <PageLayout title={title} actions={actions} icon={PAGE_ICONS.Config} padded>
            <BackupList download={downloadBackup} />
            <input ref={fileInputRef} type={'file'} style={{ display: 'none' }} />
        </PageLayout>
    );
}
